class FacetFiltersForm extends HTMLElement {
	constructor() {
		super();
		this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

		this.debouncedOnSubmit = debounce((event) => {
			this.onSubmitHandler(event);
		}, 200);

		const facetForm = this.querySelector('form');
		facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
	}

	static setListeners() {
		const onHistoryChange = (event) => {
			const searchParams = event.state
				? event.state.searchParams
				: FacetFiltersForm.searchParamsInitial;
			if (searchParams === FacetFiltersForm.searchParamsPrev) return;
			FacetFiltersForm.renderPage(searchParams, null, false);
		};
		window.addEventListener('popstate', onHistoryChange);
	}

	static toggleActiveFacets(disable = true) {
		document.querySelectorAll('[data-facets-remove]').forEach((element) => {
			element.classList.toggle('disabled', disable);
		});
	}

	static renderPage(searchParams, event, updateURLHash = true) {
		FacetFiltersForm.searchParamsPrev = searchParams;
		const sections = FacetFiltersForm.getSections();

		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
			const filterDataUrl = (element) => element.url === url;

			FacetFiltersForm.filterData.some(filterDataUrl)
				? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
				: FacetFiltersForm.renderSectionFromFetch(url, event);
		});

		if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
	}

	static renderSectionFromFetch(url, event) {
		fetch(url)
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText;
				FacetFiltersForm.filterData = [
					...FacetFiltersForm.filterData,
					{html, url},
				];
				FacetFiltersForm.renderFilters(html, event);
				FacetFiltersForm.renderProductGridContainer(html);
				FacetFiltersForm.renderProductCount(html);
				FacetFiltersForm.reviveScripts();
			});
	}

	static renderSectionFromCache(filterDataUrl, event) {
		const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
		FacetFiltersForm.renderFilters(html, event);
		FacetFiltersForm.renderProductGridContainer(html);
		FacetFiltersForm.renderProductCount(html);
		FacetFiltersForm.reviveScripts();
	}

	static renderProductGridContainer(html) {
		document.querySelector('[data-facets-products-grid]').innerHTML =
			new DOMParser()
				.parseFromString(html, 'text/html')
				.querySelector('[data-facets-products-grid]').innerHTML;
	}

	static renderProductCount(html) {
		const count = new DOMParser()
			.parseFromString(html, 'text/html')
			.querySelector('[data-facets-count]').innerHTML;
		const containers = document.querySelectorAll('[data-facets-count]');
		containers.forEach((container) => {
			container.innerHTML = count;
		});
	}

	static renderFilters(html, event) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

		const facetDetailsElements = parsedHTML.querySelectorAll(
			'[data-facets-form] [data-facets-filter]',
		);
		const matchesIndex = (element) => {
			const jsFilter = event
				? event.target.closest('[data-facets-filter]')
				: undefined;
			return jsFilter
				? element.dataset.index === jsFilter.dataset.index
				: false;
		};
		const facetsToRender = Array.from(facetDetailsElements).filter(
			(element) => !matchesIndex(element),
		);
		facetsToRender.forEach((element) => {
			document.querySelector(
				`[data-index="${element.dataset.index}"]`,
			).innerHTML = element.innerHTML;
		});

		FacetFiltersForm.renderActiveFacets(parsedHTML);
	}

	static reviveScripts() {
		const container = document.querySelector('clrz-sidebar[id="facets"]');
		const collectionGridSwitch = document.querySelector('clrz-collection-grid-switch');
		document.dispatchEvent(new CustomEvent('update-native-filters-panel', { detail : { container: container, gridSwitch: collectionGridSwitch } }));
	}

	static renderActiveFacets(html) {
		const activeFacetElementSelectors = ['[data-facets-actives]'];

		activeFacetElementSelectors.forEach((selector) => {
			const activeFacetsElement = html.querySelector(selector);
			if (!activeFacetsElement) return;
			document.querySelector(selector).innerHTML =
				activeFacetsElement.innerHTML;
		});

		FacetFiltersForm.toggleActiveFacets(false);
	}

	static updateURLHash(searchParams) {
		history.pushState(
			{searchParams},
			'',
			`${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
		);
	}

	static getSections() {
		return [
			{
				section: document.querySelector('[data-facets-product-grid]').dataset
					.id,
			},
		];
	}

	createSearchParams(form) {
		const formData = new FormData(form);
		const searchParams = new URLSearchParams(formData);

		const hasVariantOrPriceFilter =
			searchParams.get('filter.v.option') ||
			searchParams.get('filter.p.m') ||
			searchParams.get('filter.v.m') ||
			searchParams.get('filter.p.vendor') ||
			searchParams.get('filter.v.price.gte') ||
			searchParams.get('filter.v.price.lte');

		const hasAvailabilityFilter = searchParams.has('filter.v.availability');

		if (hasVariantOrPriceFilter && !hasAvailabilityFilter) {
			searchParams.append('filter.v.availability', '1');
		} else if (!hasVariantOrPriceFilter && hasAvailabilityFilter) {
			searchParams.delete('filter.v.availability');
		}

		return searchParams.toString();
	}

	onSubmitForm(searchParams, event) {
		FacetFiltersForm.renderPage(searchParams, event);
	}

	checkOverflow(container) {
		const list = container.querySelector('[data-filter-values-list]');
		if (!list) return;
		
		// Calculate total width of all visible children
		const children = Array.from(list.children);
		const contentWidth = children.reduce((total, child) => {
			if (!child.classList.contains('u-hidden')) {
				return total + child.offsetWidth;
			}
			return total;
		}, 0);
		
		// Get the maximum allowed width from CSS (25ch constraint) and add a 2px buffer
		const maxWidth = parseFloat(getComputedStyle(list).maxWidth);
		const threshold = 2;
		const isOverflowing = contentWidth > maxWidth + threshold;
				
		if (isOverflowing) {
			list.classList.add('is-overflowing');
		} else {
			list.classList.remove('is-overflowing');
		}
	}

	onSubmitHandler(event) {
		event.preventDefault();

		// displays/hide the label of the selected filter in the first level facets panel
		const filterLabel = this.querySelector(`[data-filter-label=${event.target.id}]`);
		const filterLabelParent = filterLabel.closest('[data-filter]');
		const siblings = Array.from(filterLabelParent.querySelectorAll('[data-filter-label]')).filter(li => li !== filterLabel);
		filterLabel.classList.toggle('u-hidden', !event.target.checked);

		if(event.target.type === 'radio') {
			// on radio inputs, hide other labels for the same filter
			siblings.forEach(sibling => sibling.classList.add('u-hidden'));
		} else if(event.target.type === 'checkbox') {
			// displays the number of active values for the filter
			const visibleSiblingsCount = filterLabelParent.querySelectorAll('[data-filter-label]:not(.u-hidden)').length;
			if(visibleSiblingsCount > 0) {
				filterLabelParent.querySelector('[data-active-values-count]').innerHTML = '(' + visibleSiblingsCount + ')';
			} else {
				filterLabelParent.querySelector('[data-active-values-count]').innerHTML = '';
			}
		}

		// Check if the filter values list is overflowing
		this.checkOverflow(filterLabelParent);

		const sortFilterForms = document.querySelectorAll(
			'facet-filters-form form',
		);
		const forms = [];

		sortFilterForms.forEach((form) => {
			if (form.id === 'FacetFiltersForm') {
				forms.push(this.createSearchParams(form));
			}
		});
		this.onSubmitForm(forms.join('&'), event);
	}

	onActiveFilterClick(event) {
		event.preventDefault();
		FacetFiltersForm.toggleActiveFacets();
		const url =
			event.currentTarget.href.indexOf('?') == -1
				? ''
				: event.currentTarget.href.slice(
						event.currentTarget.href.indexOf('?') + 1,
					);
		FacetFiltersForm.renderPage(url);
	}
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
	customElements.define('facet-filters-form', FacetFiltersForm);
}

FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
	constructor() {
		super();
		this.querySelectorAll('input').forEach((element) =>
			element.addEventListener('change', this.onRangeChange.bind(this)),
		);
		this.setMinAndMaxValues();
	}

	onRangeChange(event) {
		this.adjustToValidValues(event.currentTarget);
		this.setMinAndMaxValues();
	}

	setMinAndMaxValues() {
		const inputs = this.querySelectorAll('input');
		const minInput = inputs[0];
		const maxInput = inputs[1];
		if (maxInput.value) minInput.setAttribute('max', maxInput.value);
		if (minInput.value) maxInput.setAttribute('min', minInput.value);
		if (minInput.value === '') maxInput.setAttribute('min', 0);
		if (maxInput.value === '')
			minInput.setAttribute('max', maxInput.getAttribute('max'));
	}

	adjustToValidValues(input) {
		const value = Number(input.value);
		const min = Number(input.getAttribute('min'));
		const max = Number(input.getAttribute('max'));

		if (value < min) input.value = min;
		if (value > max) input.value = max;
	}
}

if (!customElements.get('price-range')) {
	customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
	constructor() {
		super();
		const facetLink = this.querySelector('a');
		facetLink.setAttribute('role', 'button');
		facetLink.addEventListener('click', this.closeFilter.bind(this));
		facetLink.addEventListener('keyup', (event) => {
			if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
		});
	}

	closeFilter(event) {
		event.preventDefault();
		const form = this.closest('facet-filters-form');
		form.onActiveFilterClick(event);
	}
}

if (!customElements.get('facet-remove')) {
	customElements.define('facet-remove', FacetRemove);
}
